import React from 'react';
import { FaFutbol, FaFootballBall, FaCheck, FaTimes } from 'react-icons/fa';
import { FaBasketball, FaVolleyball } from 'react-icons/fa6';
import { GiHockey } from 'react-icons/gi';
import { MdOutlineSportsHandball, MdSportsMma } from 'react-icons/md';
import { IoTennisball } from 'react-icons/io5';
import { SiCounterstrike } from 'react-icons/si';
import { CgMoreO } from 'react-icons/cg';
import { PiBoxingGlove, PiSoccerBallDuotone } from 'react-icons/pi';
import { useChallengeData } from '../../utils/fetchChallengeData';
import { sportColors } from '../../utils/sports';
import { SkeletonBetCard } from '../Skeleton/Skeleton';
import './Challenge.css';

// Formata data para dd-mm-yyyy
const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
        if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
            const [datePart] = dateStr.split('T');
            const [year, month, day] = datePart.split('-');
            return `${day}-${month}-${year}`;
        }
        if (typeof dateStr === 'string' && dateStr.includes('/')) {
            return dateStr.replace(/\//g, '-');
        }
        return String(dateStr);
    } catch (e) {
        return String(dateStr);
    }
};

// Formata hora para HH:MM (remove segundos)
const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const parts = String(timeStr).split(':');
    if (parts.length >= 2) {
        return `${parts[0]}:${parts[1]}`;
    }
    return String(timeStr);
};

function Challenge() {
  const { challengeData, loading, error } = useChallengeData();

  const sportIcons = {
    futebol: FaFutbol,
    basket: FaBasketball,
    voleibol: FaVolleyball,
    andebol: MdOutlineSportsHandball,
    hoquei: GiHockey,
    tenis: IoTennisball,
    futebolAmericano: FaFootballBall,
    mma: MdSportsMma,
    cs2: SiCounterstrike,
    boxe: PiBoxingGlove,
    futsal: PiSoccerBallDuotone,
    outros: CgMoreO,
  };

  function normalizeDate(dateStr) {
    if (!dateStr) return '';
    // Handle YYYY-MM-DD format (Django API)
    if (dateStr.includes('-')) {
      const [year, month, day] = dateStr.split('-').map(Number);
      return `${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/${year}`;
    }
    // Handle DD/MM/YYYY format
    const [day, month, year] = dateStr.split('/').map(Number);
    return `${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/${year}`;
  }

  if (loading) return (
    <section className="challenge-section">
      <SkeletonBetCard />
    </section>
  );
  if (error) return <div className="error-state">Erro ao carregar o desafio: {error}</div>;
  if (!challengeData) return <div className="no-bets">Ainda não há nenhuma aposta no desafio.</div>;

  const today = new Date();
  const pad = (n) => n.toString().padStart(2, '0');
  const todayStr = `${pad(today.getDate())}/${pad(today.getMonth() + 1)}/${today.getFullYear()}`;

  const isToday = normalizeDate(challengeData.date) === todayStr;
  const todaysGames = isToday ? challengeData.games : challengeData.games;

  const betResult = challengeData.result || 'pending';

  const calculatePotentialWinnings = (amount, totalOdd) => {
    return (amount * totalOdd).toFixed(2);
  };

  const potentialWinnings = calculatePotentialWinnings(challengeData.amount, challengeData.totalOdd);
  const betType = challengeData.games.length > 1 ? `Múltipla (${challengeData.games.length})` : 'Simples';

  return (
    <section className="challenge-section">
      <div className="bet-container">
        <div className="bet-card">
          <div className="bet-card-header">
            <div className="bet-header-left">
              <div className="bet-type-badge">
                {betType}
              </div>
              <span className="bet-date">{formatDate(challengeData.date)}</span>
            </div>
            <div className={`bet-result-badge ${betResult === 'won' ? 'bet-won' : betResult === 'lost' ? 'bet-lost' : 'bet-pending'}`}>
              {betResult === 'won' ? (
                <>
                  <FaCheck className="result-icon" />
                  <span>Ganho</span>
                </>
              ) : betResult === 'lost' ? (
                <>
                  <FaTimes className="result-icon" />
                  <span>Perdido</span>
                </>
              ) : (
                <span>Pendente</span>
              )}
            </div>
          </div>
          <div className="games-list">
            {todaysGames.map((game, index) => (
              <div key={game.id} className="game-item">
                <div className="game-number">
                  {sportIcons[game.sport] && React.createElement(sportIcons[game.sport], { className: "game-number-icon", style: { color: sportColors[game.sport] } })}
                </div>
                <div className="game-details">
                  <div className="game-pick-section">
                    <span className="pick-value-large">{game.pick}</span>
                    {game.pickDescription && (
                      <span className="pick-description">{game.pickDescription}</span>
                    )}
                  </div>
                  <div className="game-match">
                    <div className="game-header-row">
                      <h3 className="match-title">{game.homeTeam}{game.awayTeam ? ` vs ${game.awayTeam}` : ''}</h3>
                    </div>
                    <p className="match-datetime">
                      <span className="date">{formatDate(game.date)}</span>
                      <span className="separator">•</span>
                      <span className="time">{formatTime(game.time)}</span>
                    </p>
                  </div>
                </div>
                <div className="game-odd-display">
                  <span className="odd-label">Odd</span>
                  <span className="odd-value">{game.gameOdd}</span>
                </div>
                {index < todaysGames.length - 1 && <div className="game-divider"></div>}
              </div>
            ))}
          </div>
          <div className="bet-summary">
            <div className="summary-grid">
              <div className="summary-stat">
                <span className="stat-label">Odd Total</span>
                <span className="stat-value">{challengeData.totalOdd.toFixed(2)}</span>
              </div>
              <div className="summary-stat">
                <span className="stat-label">Montante</span>
                <span className="stat-value">{challengeData.amount.toFixed(2)}€</span>
              </div>
              <div className={`summary-stat ${betResult === 'lost' ? 'lost' : betResult === 'won' ? 'highlight' : 'pending'}`}>
                <span className="stat-label">
                  {betResult === 'won' ? 'Ganhos' : betResult === 'lost' ? 'Resultado' : 'Ganhos Possíveis'}
                </span>
                <span className="stat-value">
                  {potentialWinnings}€
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Challenge;
